import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/common/DataTable";
import { ticketService, userService, fileService, knowledgeService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Send, Lock, Globe, Paperclip, Clock, TrendingUp, Star,
  AlertTriangle, MessageSquare, History, Link2, User, BookOpen, ExternalLink,
} from "lucide-react";
import { STATUSES, PRIORITIES, STATUS_COLOR, PRIORITY_COLOR, TRANSITIONS } from "./constants";

function Section({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4 shadow-sm" data-testid={testid}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("comments");
  const [comments, setComments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [draft, setDraft] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState("");
  const [rating, setRating] = useState(0);
  const fileRef = useRef(null);
  const [kbQuery, setKbQuery] = useState("");
  const [kbResults, setKbResults] = useState([]);
  const [kbSearching, setKbSearching] = useState(false);
  const canViewKb = hasPermission("KNOWLEDGE.VIEW");

  const runKbSearch = async () => {
    setKbSearching(true);
    try {
      const d = await knowledgeService.search({ q: kbQuery, types: "article,faq", limit: 15, tenantId: ticket?.tenantId });
      setKbResults(d.results || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setKbSearching(false); }
  };

  const referenceKb = async (r) => {
    try {
      await knowledgeService.recordUsage({ knowledgeType: r.type, knowledgeId: r.id, action: "used_in_ticket", refId: id });
      toast.success(`Referenced "${r.title}" on this ticket`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canEdit = hasPermission("TICKET.EDIT");
  const canAssign = hasPermission("TICKET.ASSIGN");
  const canEscalate = hasPermission("TICKET.ESCALATE");
  const canFeedback = hasPermission("FEEDBACK.SUBMIT");

  const load = useCallback(async () => {
    try {
      const t = await ticketService.get(id);
      setTicket(t);
      const [c, tl, at] = await Promise.all([
        ticketService.comments(id), ticketService.timeline(id), ticketService.attachments(id),
      ]);
      setComments(c.items || []); setTimeline(tl.items || []); setAttachments(at.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { userService.list({ size: 200 }).then((d) => setAgents(d.items || [])).catch(() => {}); }, []);

  const act = async (fn, okMsg) => {
    setBusy("x");
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const addComment = async () => {
    if (!draft.trim()) return;
    await act(() => ticketService.addComment(id, { body: draft, visibility }), "Comment added");
    setDraft("");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "ticket");
      const up = await fileService.upload(fd);
      await ticketService.addAttachment(id, { fileId: up.id });
      toast.success("Attachment added"); await load();
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!ticket) return null;

  const sla = ticket.sla || {};
  const state = ticket.slaState || {};
  const breached = state.responseOverdue || state.resolutionOverdue;
  const validNext = TRANSITIONS[ticket.status] || [];

  return (
    <div data-testid="ticket-detail-page">
      <button onClick={() => navigate("/admin/tickets")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="ticket-back">
        <ArrowLeft className="h-4 w-4" /> Back to tickets
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-muted-foreground">{ticket.ticketNumber}</span>
            {breached && <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold" data-testid="ticket-breach-badge"><AlertTriangle className="h-3.5 w-3.5" /> SLA Breached</span>}
            {ticket.escalationLevel > 0 && <Badge color="red">Escalated L{ticket.escalationLevel}</Badge>}
          </div>
          <h1 className="text-2xl font-bold text-foreground">{ticket.subject}</h1>
          <p className="text-sm text-muted-foreground mt-1">{ticket.customerName} · {ticket.category}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={PRIORITY_COLOR[ticket.priority]}>{ticket.priority}</Badge>
          <Badge color={STATUS_COLOR[ticket.status]}>{ticket.status}</Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Description" testid="ticket-description-section">
            <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.description || "No description provided."}</p>
          </Section>

          <div className="bg-card rounded-xl border border-border shadow-sm">
            <div className="flex border-b border-border">
              {[["comments", "Comments", MessageSquare], ["timeline", "Timeline", History], ["attachments", "Attachments", Paperclip], ...(canViewKb ? [["knowledge", "Knowledge", BookOpen]] : [])].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`ticket-tab-${k}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {tab === "comments" && (
                <div className="space-y-3" data-testid="ticket-comments">
                  {comments.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No comments yet</p>}
                  {comments.map((c) => (
                    <div key={c.id} className={`rounded-lg p-3 ${c.visibility === "internal" ? "bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20" : "bg-muted/50"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{c.authorName}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          {c.visibility === "internal" ? <><Lock className="h-3 w-3" /> Internal</> : <><Globe className="h-3 w-3" /> Public</>}
                        </span>
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{c.body}</p>
                    </div>
                  ))}
                  {canEdit && (
                    <div className="pt-2 border-t border-border">
                      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Add a reply…"
                        className="ciq-input text-sm" data-testid="ticket-comment-input" />
                      <div className="flex items-center justify-between mt-2">
                        <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="bg-card border border-border rounded-lg px-2 py-1 text-xs text-foreground" data-testid="ticket-comment-visibility">
                          <option value="public">Public (customer-visible)</option>
                          <option value="internal">Internal note</option>
                        </select>
                        <button onClick={addComment} disabled={busy === "x"} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="ticket-comment-send">
                          <Send className="h-3.5 w-3.5" /> Send
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "timeline" && (
                <div className="space-y-3" data-testid="ticket-timeline">
                  {timeline.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No events</p>}
                  {timeline.map((e) => (
                    <div key={e.id} className="flex items-start gap-3 text-sm">
                      <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <span className="font-medium text-foreground">{e.action.replace(/_/g, " ")}</span>
                        <span className="text-muted-foreground"> · {e.actorEmail || "system"}</span>
                        <div className="text-xs text-muted-foreground">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === "attachments" && (
                <div className="space-y-2" data-testid="ticket-attachments">
                  {attachments.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No attachments</p>}
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm border border-border rounded-lg px-3 py-2">
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <span className="text-foreground truncate flex-1">{a.fileName}</span>
                      <span className="text-xs text-muted-foreground">{a.uploadedByName}</span>
                    </div>
                  ))}
                  {canEdit && (
                    <>
                      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="ticket-file-input" />
                      <button onClick={() => fileRef.current?.click()} disabled={busy === "upload"} className="mt-2 inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-60" data-testid="ticket-attach-btn">
                        {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Upload file
                      </button>
                    </>
                  )}
                </div>
              )}

              {tab === "knowledge" && canViewKb && (
                <div className="space-y-3" data-testid="ticket-knowledge">
                  <div className="flex items-center gap-2">
                    <input value={kbQuery} onChange={(e) => setKbQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runKbSearch()}
                      placeholder="Search knowledge to help resolve this ticket…" className="ciq-input text-sm" data-testid="ticket-kb-input" />
                    <button onClick={runKbSearch} disabled={kbSearching} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 shrink-0" data-testid="ticket-kb-search">
                      {kbSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />} Search
                    </button>
                  </div>
                  {kbResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Search the knowledge base to find relevant articles and FAQs, then reference them on this ticket.</p>
                  ) : (
                    <div className="space-y-2">
                      {kbResults.map((r) => (
                        <div key={`${r.type}-${r.id}`} className="rounded-lg border border-border p-3" data-testid={`ticket-kb-result-${r.id}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-sm font-medium text-foreground truncate">{r.title}</span>
                            <span className="text-xs text-muted-foreground shrink-0 uppercase">{r.type}</span>
                          </div>
                          {r.snippet && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{r.snippet}</p>}
                          <div className="flex items-center gap-3">
                            <button onClick={() => referenceKb(r)} className="text-xs font-semibold text-primary hover:underline" data-testid={`ticket-kb-reference-${r.id}`}>Reference on ticket</button>
                            {r.type === "article" && (
                              <button onClick={() => navigate(`/admin/knowledge/articles/${r.id}`)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`ticket-kb-open-${r.id}`}><ExternalLink className="h-3 w-3" /> Open</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Section title="SLA" testid="ticket-sla-panel">
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Resolution due</span>
                <span className={state.resolutionOverdue ? "text-red-600 font-semibold" : "text-foreground"}>
                  {sla.resolutionDueAt ? sla.resolutionDueAt.slice(0, 16).replace("T", " ") : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Remaining</span>
                <span className={state.resolutionOverdue ? "text-red-600 font-semibold" : "text-foreground"}>
                  {state.resolutionMinsRemaining != null
                    ? (state.resolutionMinsRemaining < 0 ? `${Math.abs(state.resolutionMinsRemaining)}m overdue` : `${state.resolutionMinsRemaining}m`)
                    : "Done"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">First response</span>
                <Badge color={sla.firstResponseAt ? "green" : "amber"}>{sla.firstResponseAt ? "Met" : "Pending"}</Badge>
              </div>
            </div>
          </Section>

          {canEdit && (
            <Section title="Workflow" testid="ticket-workflow">
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select value={ticket.status} disabled={busy === "x"}
                onChange={(e) => act(() => ticketService.setStatus(id, { status: e.target.value }), `Status: ${e.target.value}`)}
                className="ciq-input mb-3" data-testid="ticket-status-select">
                <option value={ticket.status}>{ticket.status} (current)</option>
                {validNext.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="block text-xs text-muted-foreground mb-1">Priority</label>
              <select value={ticket.priority} disabled={busy === "x"}
                onChange={(e) => act(() => ticketService.setPriority(id, { priority: e.target.value }), "Priority updated")}
                className="ciq-input mb-3" data-testid="ticket-priority-select">
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
              {canAssign && (
                <>
                  <label className="block text-xs text-muted-foreground mb-1">Assigned agent</label>
                  <select value={ticket.assignedAgentId || ""} disabled={busy === "x"}
                    onChange={(e) => act(() => ticketService.assign(id, { assignedAgentId: e.target.value || null }), "Assigned")}
                    className="ciq-input" data-testid="ticket-assign-select">
                    <option value="">Unassigned</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{`${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email}</option>)}
                  </select>
                </>
              )}
            </Section>
          )}

          {canEscalate && (
            <Section title="Escalation" testid="ticket-escalation">
              <p className="text-sm text-muted-foreground mb-2">Current level: <span className="font-semibold text-foreground">L{ticket.escalationLevel}</span></p>
              <button onClick={() => act(() => ticketService.escalate(id, { reason: "Manually escalated" }), "Escalated")}
                disabled={busy === "x"} className="w-full inline-flex items-center justify-center gap-1.5 border border-amber-300 text-amber-700 dark:text-amber-400 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-60" data-testid="ticket-escalate-btn">
                <TrendingUp className="h-4 w-4" /> Escalate now
              </button>
            </Section>
          )}

          <Section title="Links" testid="ticket-links">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-muted-foreground" /> <span className="text-foreground">{ticket.customerName || "—"}</span></div>
              {ticket.conversationId
                ? <button onClick={() => navigate("/admin/inbox")} className="flex items-center gap-2 text-primary hover:underline" data-testid="ticket-conv-link"><Link2 className="h-3.5 w-3.5" /> Linked conversation</button>
                : <div className="flex items-center gap-2 text-muted-foreground"><Link2 className="h-3.5 w-3.5" /> No conversation linked</div>}
              {ticket.caseId && <div className="flex items-center gap-2 text-muted-foreground"><Link2 className="h-3.5 w-3.5" /> Part of a case</div>}
            </div>
          </Section>

          {canFeedback && ticket.status === "Resolved" && !ticket.feedbackId && (
            <Section title="Record CSAT" testid="ticket-feedback">
              {ticket.feedbackToken && (
                <div className="mb-3 pb-3 border-b border-border">
                  <p className="text-xs text-muted-foreground mb-1">Public CSAT link (send to customer)</p>
                  <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/feedback/${ticket.feedbackToken}`); toast.success("CSAT link copied"); }}
                    className="w-full inline-flex items-center justify-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs hover:bg-muted" data-testid="ticket-copy-csat-link">
                    <Link2 className="h-3.5 w-3.5" /> Copy public feedback link
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground mb-2">Or record internally:</p>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} onClick={() => setRating(i)} data-testid={`ticket-csat-${i}`}>
                    <Star className={`h-6 w-6 ${i <= rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30"}`} />
                  </button>
                ))}
              </div>
              <button onClick={() => rating && act(() => ticketService.feedback(id, { rating }), "Feedback recorded")}
                disabled={!rating || busy === "x"} className="w-full bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="ticket-feedback-save">
                Save rating
              </button>
            </Section>
          )}
          {ticket.feedbackId && (
            <Section title="CSAT" testid="ticket-csat-done">
              <div className="flex items-center gap-2"><Star className="h-5 w-5 text-amber-400 fill-amber-400" /> <span className="font-semibold text-foreground">{ticket.csat}/5</span></div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
